/**
 * `cancellationRequested` slice mutations — Multi-Add Phase 2 W6
 * (PRD §2.10).
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global): the primitive
 * lives in `types.ts::source.cancellationRequested`; this module owns the
 * pure functional mutations against an immer draft. Mirror of the pattern
 * already shipped by W3 (`failed-devices.slice.ts`).
 *
 * Per `think-in-systems` (ai.knowledge/global): the slice is a single
 * boolean — minimal primitive. The mutations are thin functional shims
 * so callers (`scaffold.tsx`'s Cancel button, `wiring.ts`'s batch reset)
 * stay framework-agnostic and testable without zustand/React.
 *
 * The slice exposes:
 *   - `requestCancellation` — flip flag to `true` (Cancel click)
 *   - `clearCancellation`   — reset to `false` (new batch start)
 *   - `isCancellationRequested` — read accessor for renderer subscribers
 *
 * Scope is per-room (Provider re-mounts on room change); a batch can
 * span at most that lifetime, so the flag resets implicitly on room-leave
 * via the store re-init.
 */
import type { Draft, State } from './types.js';

/**
 * Flip the cancellation flag to `true`.
 *
 * The call site is the Cancel button on the `bulk-progress` overlay.
 * The flag is checked at the TOP of every per-device iteration in
 * `runBulkCommit`; the in-flight step (if any) is allowed to settle
 * before the loop breaks (no kill — §2.10 explicitly requires graceful
 * exit, not Promise abort).
 *
 * Idempotent: calling twice is a no-op. The Cancel button on the overlay
 * disables itself after the first click but defensive idempotency means a
 * keyboard-spam scenario doesn't matter.
 */
export const requestCancellation = (draft: Draft): void => {
  draft.source.cancellationRequested = true;
};

/**
 * Reset the cancellation flag to `false`.
 *
 * Called by `handleBulkCommit` in wiring.ts at the start of every batch.
 * Without this reset a flag set in a prior batch (between Cancel click
 * and the next "Add N Sources" click) would short-circuit the new run on
 * the very first iteration — the user would see "no devices added"
 * without any visible cause.
 */
export const clearCancellation = (draft: Draft): void => {
  draft.source.cancellationRequested = false;
};

/**
 * Read-only accessor — returns the current cancellation flag value.
 *
 * Renderer hooks read this via the store selector
 * (`useStore((s) => s.source.cancellationRequested)`); the helper exists
 * for parity with W3's `getAllFailedDevices` so the slice's read surface
 * is a single named export the test suite + wiring.ts can both import.
 */
export const isCancellationRequested = (state: State): boolean =>
  state.source.cancellationRequested;
